import type { ProjectTypeKey } from "store/types/project";
import type { BucketShare, BenchmarkBucket } from "./budgetBucketClassification";

export type BenchmarkStatus = "typical" | "high" | "low";

export type BenchmarkSource = "industry" | "peers";

export interface BenchmarkRange {
  min: number;
  typical: number;
  max: number;
}

export type BenchmarkSet = Record<BenchmarkBucket, BenchmarkRange>;

export interface BucketComparison {
  bucket: BenchmarkBucket;
  sharePercent: number;
  estimate: number;
  status: BenchmarkStatus;
  deltaFromTypical: number;
  range: BenchmarkRange;
}

export interface BenchmarkComparison {
  productionType: ProjectTypeKey;
  source: BenchmarkSource;
  buckets: BucketComparison[];
  overallTypical: boolean;
  summary: string;
}

const BUCKET_LABELS: Record<BenchmarkBucket, string> = {
  atl: "ATL",
  production: "Production",
  post: "Post",
  other: "Other",
};

const PRODUCTION_TYPE_LABELS: Record<ProjectTypeKey, string> = {
  short: "short film",
  feature: "feature",
  documentary: "documentary",
  studio: "studio feature",
  commercial: "commercial",
  other: "production",
};

export const BENCHMARKS: Record<ProjectTypeKey, BenchmarkSet> = {
  feature: {
    atl: { min: 25, typical: 30, max: 35 },
    production: { min: 40, typical: 45, max: 50 },
    post: { min: 10, typical: 15, max: 20 },
    other: { min: 5, typical: 8, max: 10 },
  },
  studio: {
    atl: { min: 28, typical: 32, max: 38 },
    production: { min: 38, typical: 43, max: 48 },
    post: { min: 10, typical: 14, max: 18 },
    other: { min: 5, typical: 8, max: 10 },
  },
  short: {
    atl: { min: 10, typical: 15, max: 20 },
    production: { min: 45, typical: 50, max: 58 },
    post: { min: 12, typical: 18, max: 25 },
    other: { min: 3, typical: 5, max: 8 },
  },
  documentary: {
    atl: { min: 12, typical: 18, max: 24 },
    production: { min: 38, typical: 44, max: 52 },
    post: { min: 15, typical: 20, max: 28 },
    other: { min: 4, typical: 7, max: 10 },
  },
  commercial: {
    atl: { min: 12, typical: 16, max: 22 },
    production: { min: 42, typical: 48, max: 55 },
    post: { min: 8, typical: 12, max: 18 },
    other: { min: 3, typical: 5, max: 8 },
  },
  other: {
    atl: { min: 20, typical: 28, max: 35 },
    production: { min: 40, typical: 46, max: 52 },
    post: { min: 10, typical: 15, max: 22 },
    other: { min: 4, typical: 7, max: 10 },
  },
};

const DEFAULT_PRODUCTION_TYPE: ProjectTypeKey = "feature";

export function normalizeProductionType(value: string | undefined | null): ProjectTypeKey {
  if (!value) return DEFAULT_PRODUCTION_TYPE;
  const normalized = value.trim().toLowerCase();
  if (normalized in BENCHMARKS) {
    return normalized as ProjectTypeKey;
  }
  return DEFAULT_PRODUCTION_TYPE;
}

export function getBenchmarkSet(
  productionType: ProjectTypeKey | string | undefined | null,
  source: BenchmarkSource = "industry",
  peerSet?: BenchmarkSet | null
): BenchmarkSet {
  if (source === "peers" && peerSet) {
    return peerSet;
  }
  return BENCHMARKS[normalizeProductionType(productionType)];
}

export function getBucketLabel(bucket: BenchmarkBucket): string {
  return BUCKET_LABELS[bucket];
}

export function getProductionTypeLabel(productionType: ProjectTypeKey): string {
  return PRODUCTION_TYPE_LABELS[productionType];
}

function classifyShare(sharePercent: number, range: BenchmarkRange): BenchmarkStatus {
  if (sharePercent < range.min) return "low";
  if (sharePercent > range.max) return "high";
  return "typical";
}

function buildSummary(
  productionType: ProjectTypeKey,
  source: BenchmarkSource,
  buckets: BucketComparison[],
  overallTypical: boolean
): string {
  const typeLabel = getProductionTypeLabel(productionType);
  const sourceLabel = source === "peers" ? "RivetAI peer budgets" : "industry benchmarks";

  if (overallTypical) {
    return `Within typical range for a ${typeLabel} compared to ${sourceLabel}.`;
  }

  const outliers = buckets
    .filter((bucket) => bucket.status !== "typical")
    .map((bucket) => `${getBucketLabel(bucket.bucket)} ${bucket.status}`)
    .join(", ");

  return `${outliers} outside typical range for a ${typeLabel} compared to ${sourceLabel}. Worth a second look.`;
}

export function compareToBenchmark(
  shares: BucketShare[],
  benchmarkSet: BenchmarkSet,
  productionType: ProjectTypeKey,
  source: BenchmarkSource = "industry"
): BenchmarkComparison {
  const buckets: BucketComparison[] = shares.map((share) => {
    const range = benchmarkSet[share.bucket];
    const status = classifyShare(share.sharePercent, range);
    return {
      bucket: share.bucket,
      sharePercent: share.sharePercent,
      estimate: share.estimate,
      status,
      deltaFromTypical: share.sharePercent - range.typical,
      range,
    };
  });

  const overallTypical = buckets.every((bucket) => bucket.status === "typical");

  return {
    productionType,
    source,
    buckets,
    overallTypical,
    summary: buildSummary(productionType, source, buckets, overallTypical),
  };
}

export function benchmarkSetFromCatalogRows(
  rows: Array<{
    bucket: BenchmarkBucket;
    min_pct: number;
    typical_pct: number;
    max_pct: number;
  }>
): BenchmarkSet | null {
  if (rows.length === 0) return null;

  const set = {} as BenchmarkSet;
  rows.forEach((row) => {
    set[row.bucket] = {
      min: row.min_pct,
      typical: row.typical_pct,
      max: row.max_pct,
    };
  });

  const hasAllBuckets = (["atl", "production", "post", "other"] as BenchmarkBucket[]).every(
    (bucket) => set[bucket] != null
  );

  return hasAllBuckets ? set : null;
}
